import {
  defaultShouldDehydrateQuery,
  isServer,
  QueryClient,
  type QueryFunction,
  type QueryKey,
} from '@tanstack/react-query';
import axios from 'axios';
import qs from 'qs';

const isSingleArray = (queryKey: QueryKey): queryKey is [string] => {
  return queryKey.length === 1;
};

const defaultQueryFn: QueryFunction = async ({queryKey, signal}) => {
  let url = '';
  let params = {};

  if (isSingleArray(queryKey)) {
    [url] = queryKey;
  } else {
    const [maybeUrl, maybeParams] = queryKey;
    if (typeof maybeUrl === 'string') url = maybeUrl;
    if (maybeParams && typeof maybeParams === 'object') params = maybeParams;
  }

  const {data} = await axios.get(url, {
    params,
    signal,
    paramsSerializer: p => qs.stringify(p, {arrayFormat: 'repeat'}),
  });
  return data;
};

// const queryClient = new QueryClient({
//     defaultOptions: {
//         queries: {
//             queryFn: defaultQueryFn,
//         },
//     },
// });

// export default queryClient;

// ---------------------------------

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
        retry: (failureCount, error) => {
          // Don't retry on 4xx errors
          if (error && typeof error === 'object' && 'status' in error) {
            const status = (error as any).status;
            if (status >= 400 && status < 500) return false;
          }
          return failureCount < 3;
        },
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        queryFn: defaultQueryFn,
      },
      mutations: {
        retry: false,
      },
      dehydrate: {
        shouldDehydrateQuery: query => defaultShouldDehydrateQuery(query) || query.state.status === 'pending',
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined;

export function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}
